import { Button, Icon, Toast } from '@/components'
import classNames from 'classnames'
import { useWallet } from '../providers/wallet'
import { ButtonLink } from '@/components/Button'
import { getParameterByName } from '@/util/queryHelpers'
import { useMigrateListingsMutation } from '@/api/resources/user/listings/migrate'
import { pluralize } from '@/util/stringHelpers'
import { useInventory } from '@/hooks/useInventory'

export enum WalletContextualBannerState {
  Migrate = 0,
  ResumeMigrate = 1,
  ReviewInactive = 2,
  ReactivateListings = 3
}

type Props = {
  className?: string
  inActiveListingsCount?: number
}

const baseTitleClasses = 'h6'
const baseDescriptionClasses = 'body-sm text-gray-300'

const WalletContextualBanner = ({
  className,
  inActiveListingsCount
}: Props) => {
  const { migrationNeeded, migrationStep } = useWallet()
  const hasListingsFilter = getParameterByName('show_only_listings')
  const hasInactiveListings = inActiveListingsCount > 0

  const { refreshAssets } = useInventory()

  const { mutate: migrateListings, isLoading } = useMigrateListingsMutation({
    onSuccess: () => {
      refreshAssets()
      Toast({
        type: 'success',
        description: 'All Listings Reactivated Successfully'
      })
    }
  })

  if (!migrationNeeded && !hasInactiveListings) {
    return null
  }

  let bannerState: WalletContextualBannerState
  if (migrationNeeded) {
    if (migrationStep === 0) {
      // has not completed a migration step
      bannerState = WalletContextualBannerState.Migrate
    } else {
      // has completed a migration step
      bannerState = WalletContextualBannerState.ResumeMigrate
    }
  } else {
    // has migrated and has inactive listings
    if (!hasListingsFilter) {
      bannerState = WalletContextualBannerState.ReviewInactive
    }
  }

  const handleActivateAllListingsClick = async () => {
    migrateListings()
  }

  const getBannerCopy = () => {
    switch (bannerState) {
      case WalletContextualBannerState.Migrate:
        return (
          <>
            <div className={baseTitleClasses}>
              Migrate wallet to use Droppp marketplace
            </div>
            <div className={baseDescriptionClasses}>
              Any active listings you had are currently inactive until you
              migrate to a new Droppp Wallet.
            </div>
          </>
        )
      case WalletContextualBannerState.ResumeMigrate:
        return (
          <>
            <div className={baseTitleClasses}>
              Migrate wallet to reactivate your listings
            </div>
            <div className={baseDescriptionClasses}>
              To continue using Droppp Marketplace, you’ll need to finish
              creating a Droppp Wallet.
            </div>
          </>
        )
      case WalletContextualBannerState.ReviewInactive:
        return (
          <>
            <div className="flex items-center gap-1">
              <Icon name="alertYellow" className="w-[13px] h-[13px]" />
              <div
                className={baseTitleClasses}
              >{`You have ${inActiveListingsCount} Inactive ${pluralize(
                'Listing',
                inActiveListingsCount
              )}`}</div>
            </div>
            <div className={baseDescriptionClasses}>
              Review and reactivate your inactive listings.
            </div>
          </>
        )
      default:
        return (
          <>
            <div className="flex items-center gap-1">
              <Icon name="alertYellow" className="w-[13px] h-[13px]" />
              <div
                className={baseTitleClasses}
              >{`You have ${inActiveListingsCount} Inactive ${pluralize(
                'Listing',
                inActiveListingsCount
              )}`}</div>
            </div>
            <div className={baseDescriptionClasses}>
              Update your listings individually or reactivate them all at once.
            </div>
          </>
        )
    }
  }

  const getButton = () => {
    switch (bannerState) {
      case WalletContextualBannerState.Migrate:
        return (
          <ButtonLink theme="solana" href="/wallet/migrate">
            Migrate Wallet
          </ButtonLink>
        )
      case WalletContextualBannerState.ResumeMigrate:
        return (
          <ButtonLink theme="solana" href="/wallet/migrate">
            Resume Migration
          </ButtonLink>
        )
      case WalletContextualBannerState.ReviewInactive:
        return (
          <ButtonLink theme="blue" href="/inventory?show_only_listings=true">
            Review Inactive Listings
          </ButtonLink>
        )
      default:
        return (
          <Button
            theme="green-cta"
            onClick={handleActivateAllListingsClick}
            loading={isLoading}
          >
            Reactivate All Listings
          </Button>
        )
    }
  }

  return (
    <div
      className={classNames(
        'flex flex-col lg:flex-row lg:items-center justify-between gap-[12px] w-full bg-gray-850 rounded-3xl min-h-6 py-2 px-3',
        className
      )}
    >
      <div className="flex flex-col gap-1">{getBannerCopy()}</div>
      {getButton()}
    </div>
  )
}

export default WalletContextualBanner
